import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect } from 'react'

import { AsyncStateN } from '@blade/std-lib'
import { networkDataNativePriceStateAtom } from '@root/entities/network'
import { loadWalletsAction } from '@root/entities/wallet/atoms'

import { AccountMenu } from './account-menu'
import { useSpendable } from './spendable'

/**
 * The bar across the top of every V2 screen.
 *
 * It lived inside the Pulse screen while Pulse was the only screen in this
 * language. The portfolio is the second, and a copied header is a header that
 * drifts — one gets a link the other does not, the brand mark moves two pixels
 * on one of them. So it is one component, and which entry is lit is a prop.
 */
export const BoardNav: React.FC<{ active: BoardTab }> = memo(({ active }) => {
  /*
   * The account's wallets, asked for here because this bar is on every V2
   * screen and every one of them needs them: the balance in this bar, the
   * figures on the portfolio, what a buy spends from on the board.
   */
  const loadWallets = useAction(loadWalletsAction)
  useEffect(() => loadWallets(), [loadWallets])

  return (
    <header className="nav">
      <img className="mark" src="/favicon.svg" alt="kol.trade" />
      <span className="brand">kol.trade</span>

      {TABS.map((tab) => (
        <a
          key={tab.key}
          className={tab.key === active ? 'nv on' : 'nv'}
          href={tab.href}
        >
          {tab.label}
        </a>
      ))}

      <div className="navright">
        <label className="search lit">
          <svg
            className="io"
            viewBox="0 0 24 24"
            style={{ color: 'var(--t4)' }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.7-3.7" />
          </svg>
          <input
            type="text"
            placeholder="Search by ticker or CA"
            aria-label="Search by ticker or contract address"
          />
          <span className="kbd">/</span>
        </label>
        <SolPrice />
        <WalletBalance />
        <button className="cta" type="button">
          <svg className="is" viewBox="0 0 24 24">
            <path d="M13.2 2 4 14h5.8l-1 8L18 10h-5.8l1.2-8Z" />
          </svg>
          Deposit
        </button>
        <AccountMenu />
        <button className="ib burger" type="button" aria-label="Menu">
          <svg
            className="io"
            viewBox="0 0 24 24"
            style={{ width: 16, height: 16 }}
          >
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
      </div>
    </header>
  )
})

export type BoardTab =
  | 'discover'
  | 'pulse'
  | 'trackers'
  | 'portfolio'
  | 'rewards'

const TABS: ReadonlyArray<{ key: BoardTab; label: string; href: string }> = [
  { key: 'discover', label: 'Discover', href: '/' },
  { key: 'pulse', label: 'Pulse', href: '/pulse-v2' },
  { key: 'trackers', label: 'Trackers', href: '/wallet-tracker' },
  { key: 'portfolio', label: 'Portfolio', href: '/portfolio-v2' },
  { key: 'rewards', label: 'Rewards', href: '/profile' },
]

/**
 * The live native price.
 *
 * The figure is the one the rest of the terminal reads. Until it arrives none
 * is shown — the prototype's +2.1% had no source and is not invented here.
 */
const SolPrice: React.FC = memo(() => {
  const [priceState] = useAtom(networkDataNativePriceStateAtom)

  const price = AsyncStateN.matchWaiting(priceState, {
    Waiting: () => undefined,
    Error: () => undefined,
    Success: (value) => value,
  })

  return (
    <div className="pill lit">
      <span className="lb">SOL</span>
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {price ? `$${price.toFixed(2)}` : '—'}
      </span>
    </div>
  )
})

/** What the signed-in user can spend, in the units their mode trades in. */
const WalletBalance: React.FC = memo(() => {
  const spendable = useSpendable()

  return (
    <div className="pill lit">
      <svg className="io" viewBox="0 0 24 24" style={{ color: 'var(--t4)' }}>
        <rect x="3" y="6" width="18" height="13" rx="2.5" />
        <path d="M16.5 12.5h.01" />
      </svg>
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {spendable ?? '—'}
      </span>
    </div>
  )
})
